import postsMeta from 'virtual:posts-index'
import { parseFrontmatter, render } from './md'

export type PostLocale = 'en' | 'fr' | 'es'
export const POST_LOCALES: readonly PostLocale[] = ['en', 'fr', 'es']

export interface PostMeta {
  locale: PostLocale
  /** Slug of the canonical English source post shared by every translation. */
  sourceSlug: string
  file: string
  slug: string
  route: string // English: /YYYY/MM/DD/slug/; translations: /{locale}/YYYY/MM/DD/slug/
  title: string
  description: string
  date: string
  modified: string
  category: string
  author: string
  reviewedBy: string
  thumbnail: string
  tags: string[]
  related: string[]
}

// Lightweight metadata for every post (from the build-time virtual index).
const metas = postsMeta as PostMeta[]

export function getAllPosts(locale: PostLocale = 'en'): PostMeta[] {
  return metas.filter((post) => post.locale === locale)
}

export function getPostMeta(slug: string, locale: PostLocale = 'en'): PostMeta | undefined {
  return metas.find((p) => p.locale === locale && p.slug === slug)
}

/** Find a translated sibling by its English source slug. */
export function getPostTranslation(sourceSlug: string, locale: PostLocale): PostMeta | undefined {
  return metas.find((p) => p.locale === locale && p.sourceSlug === sourceSlug)
}

export function getPostAlternates(post: PostMeta): PostMeta[] {
  return metas.filter((candidate) => candidate.sourceSlug === post.sourceSlug)
}

/** Map a category name to its name in another locale via any post that exists
 * in both languages. Category names are translated per locale, so slugs can't
 * be reused across languages. */
export function getCategoryTranslation(category: string, from: PostLocale, to: PostLocale): string | undefined {
  if (from === to) return category
  for (const post of metas) {
    if (post.locale !== from || post.category !== category) continue
    const sibling = metas.find((candidate) => candidate.sourceSlug === post.sourceSlug && candidate.locale === to)
    if (sibling?.category) return sibling.category
  }
  return undefined
}

/** Prefer reviewed recommendations, then fill the remaining slots with posts
 * from the same category so every rendered related-post card has a real URL. */
export function getRelatedPosts(post: PostMeta, limit = 3): PostMeta[] {
  const curated = post.related
    .map((slug) => getPostMeta(slug, post.locale))
    .filter((candidate): candidate is PostMeta => candidate !== undefined && candidate.slug !== post.slug)

  const selected = new Set(curated.map((candidate) => candidate.slug))
  const fallback = metas.filter((candidate) => (
    candidate.locale === post.locale
    && candidate.slug !== post.slug
    && candidate.category === post.category
    && !selected.has(candidate.slug)
  ))

  return [...curated, ...fallback].slice(0, limit)
}

export function getAllCategories(locale: PostLocale = 'en'): string[] {
  return getAllPosts(locale).map((p) => p.category).filter(Boolean).filter((cat, i, all) => all.indexOf(cat) === i)
}

export function categorySlug(name = ''): string {
  const normalized = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  return normalized.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

// Bodies are loaded lazily per route so they never bloat the initial bundle.
const bodyLoaders = import.meta.glob('/content/posts/*/*.md', { query: '?raw', import: 'default' })

export async function loadPostHtml(meta: PostMeta): Promise<{ meta: PostMeta; html: string } | undefined> {
  const loader = bodyLoaders[`/content/posts/${meta.locale}/${meta.file}`]
  if (!loader) return { meta, html: '' }
  const raw = (await loader()) as string
  const { content } = parseFrontmatter(raw)
  return { meta, html: render(content) }
}
